import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const AdminLoginPage = () => {
  const { user, checking, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (checking) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <p className="text-sm text-brand-inkSoft">Checking session…</p>
      </div>
    );
  }
  if (user) return <Navigate to="/admin/leads" replace />;

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!email || !password) {
      toast.error("Enter email and password.");
      return;
    }
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Signed in.");
      navigate("/admin/leads");
    } else {
      toast.error(res.error || "Login failed.");
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-brand-bg flex items-center justify-center px-6 py-16"
    >
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <div className="mx-auto w-14 h-14 bg-brand-ink flex items-center justify-center mb-6">
            <Lock className="w-5 h-5 text-brand-cta" strokeWidth={1.75} />
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-cta mb-3">
            Admin
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-brand-ink leading-[1]">
            Sign in to JMJC.
          </h1>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="bg-brand-surface border border-brand-border p-8 lg:p-10 flex flex-col gap-6"
          noValidate
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-inkSoft">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              autoComplete="email"
              className="w-full bg-transparent border-b border-brand-border focus:border-brand-cta py-3 text-base text-brand-ink outline-none transition-colors"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-inkSoft">
              Password
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              autoComplete="current-password"
              className="w-full bg-transparent border-b border-brand-border focus:border-brand-cta py-3 text-base text-brand-ink outline-none transition-colors"
            />
          </label>

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="mt-2 inline-flex items-center justify-between gap-3 px-6 py-4 bg-brand-cta hover:bg-brand-ctaHover text-white text-xs font-bold uppercase tracking-[0.16em] transition-colors disabled:opacity-60"
          >
            {submitting ? "Signing in…" : "Sign in"}
            <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
          </button>
        </form>

        <p className="text-xs text-brand-inkSoft mt-6 text-center">
          Restricted access. For JMJC team only.
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
